/**
 * Chart by Player 세로 차트 — 선수를 세로로 나열하고 값을 가로로 그린다 (일감 #185, 2026-09-30 결정).
 *
 * 기존 엔진(multiple.ts createChart)은 선수가 X축인 가로 차트만 그리고 축을 전치할 수 없어, 세로 차트는 여기서
 * d3 로 새로 그린다. 종류·조합은 가로와 같다 — 막대·누적 막대·포인트·라인·에어리어·혼합. 왼/오른 Y축은
 * 아래쪽에 시리즈마다 한 줄씩 쌓이는 X축이 된다. 값 표시·소수·단위·수동 스케일·정렬 규칙은 가로와 같다.
 * 리포트 화면(VerticalPlayerChart.tsx)·리포트 PDF(PrintableChartByPlayerSection)·PDF 빌더(BuilderD3Chart)가 공유한다.
 *
 * 한 줄(선수) 높이는 고정(VERTICAL_CHART_ROW_PX)이라 선수가 많으면 호출한 쪽이 페이지 단위(기본 25명)로 잘라 넘긴다.
 * 여러 페이지가 같은 스케일을 쓰도록 highest(시리즈별 최대)·highestStackedSum(누적 막대 합 최대)을 받는다.
 */
import * as d3 from "d3"

import type { AxisY, ChartRow } from "@/app/report/common/constructor"
import { CHART_TYPE, VALUE_POSITION_TYPE } from "@/app/report/common/constants"
import { getLocaleString } from "@/app/report/common/tools"
import { colors } from "@/components/colors"

/** 선수 한 줄 높이(px) */
export const VERTICAL_CHART_ROW_PX = 28
/** 시리즈 축 한 줄 높이(px) — 아래쪽에 시리즈마다 하나씩 쌓인다 */
export const VERTICAL_CHART_AXIS_PX = 26
export const VERTICAL_CHART_MARGIN = { top: 6, right: 64, bottom: 6, left: 8 }
/** 페이지(줄 묶음)당 선수 수 — 기본 25, 세로 용지 최대 30, 가로 용지 최대 20 (2026-09-30 결정) */
export const VERTICAL_PLAYERS_PER_PAGE_DEFAULT = 25
export const VERTICAL_PLAYERS_PER_PAGE_MAX_PORTRAIT = 30
export const VERTICAL_PLAYERS_PER_PAGE_MAX_LANDSCAPE = 20

const LABEL_MIN_PX = 72
const LABEL_MAX_PX = 168
const LABEL_CHAR_PX = 6.4
const BAR_TYPES = [CHART_TYPE.BAR]
const STACKED_TYPES = [CHART_TYPE.STACKED_BAR]

export interface VerticalPlayerChartInput {
  rows: ChartRow[]
  axisY: AxisY[]
  /** 그릴 폭(px). 선수 이름 열과 오른쪽 값 여백을 포함한다 */
  width: number
  rowHeight?: number
  valuePositionType?: VALUE_POSITION_TYPE
  /** 값 표시 소수 자릿수 — null 이면 시리즈(AxisY.decimalPoint) 값 */
  decimalPoint?: number | null
  /** 시리즈별 최대값(모든 페이지 공통 스케일). 없으면 넘긴 행에서 구한다 */
  highest?: Array<number | null>
  /** 누적 막대 합의 최대값(모든 페이지 공통). 없으면 넘긴 행에서 구한다 */
  highestStackedSum?: number | null
  fontSize?: number
  /** 아래쪽 시리즈 X축(값 눈금) 표시 — 기본 true (#185) */
  showAxis?: boolean
  onRowHover?: (rowIndex: number | null, event: MouseEvent | null) => void
}

type Series = { axis: AxisY; index: number }

/** 같은 단위끼리 2개 이상 모인 누적 묶음 — 가로 엔진과 같은 규칙. 짝이 없는 누적은 보통 막대로 그린다 */
export function stackedUnitGroups(axisY: AxisY[]): number[][] {
  const byUnit = new Map<string, number[]>()
  axisY.forEach((axis, index) => {
    if (STACKED_TYPES.includes(axis.chartType)) {
      const unit = axis.unit || "default"
      byUnit.set(unit, [...(byUnit.get(unit) ?? []), index])
    }
  })

  return [...byUnit.values()].filter((group) => group.length > 1)
}

/** 시리즈 축 줄 수 — 막대는 시리즈마다 하나, 누적 막대는 묶음에 하나, 나머지는 시리즈마다 하나 */
export function countVerticalAxisRows(axisY: AxisY[]): number {
  const groups = stackedUnitGroups(axisY)
  const members = groups.reduce((sum, group) => sum + group.length, 0)

  return axisY.length - members + groups.length
}

/** 그려질 전체 높이(px) — 칸 높이·페이지 계산용 */
export function measureVerticalChartHeight(
  rowCount: number,
  axisY: AxisY[],
  rowHeight?: number,
  showAxis = true,
): number {
  return (
    VERTICAL_CHART_MARGIN.top +
    rowCount * (rowHeight ?? rowHeightFor(axisY)) +
    (showAxis ? countVerticalAxisRows(axisY) : 0) * VERTICAL_CHART_AXIS_PX +
    VERTICAL_CHART_MARGIN.bottom
  )
}

/** 세로 차트는 막대·누적 막대만 — 그 외 종류는 막대로 바꿔 그린다 (2026-09-30 결정) */
export function coerceVerticalAxisTypes(axisY: AxisY[]): AxisY[] {
  return axisY.map((axis) =>
    BAR_TYPES.includes(axis.chartType) || STACKED_TYPES.includes(axis.chartType)
      ? axis
      : ({ ...axis, chartType: CHART_TYPE.BAR } as AxisY),
  )
}

/** 시리즈별 최대값 — 여러 페이지가 같은 스케일을 쓰게 호출한 쪽이 전체 행으로 미리 구해 넘긴다 */
export function collectVerticalHighest(rows: ChartRow[], axisY: AxisY[]) {
  const highest = axisY.map((_, index) =>
    rows.reduce<number>((max, row) => Math.max(max, row.data[index] ?? 0), 0),
  )
  // 누적 묶음은 첫 묶음 기준 — 묶음이 둘 이상이면 렌더러가 묶음별로 다시 구한다
  const stackedIndexes = stackedUnitGroups(axisY)[0] ?? []
  const highestStackedSum = rows.reduce<number>(
    (max, row) =>
      Math.max(
        max,
        stackedIndexes.reduce((sum, i) => sum + (row.data[i] ?? 0), 0),
      ),
    0,
  )

  return { highest, highestStackedSum }
}

/** 선수 한 줄 높이 — 막대 칸(막대 시리즈 수 + 누적 묶음)이 많을수록 높인다: 1칸 28, 2칸 38, 3칸 48 (값이 막대 안에 들어가게) */
export function rowHeightFor(axisY: AxisY[], base = VERTICAL_CHART_ROW_PX): number {
  const groups = stackedUnitGroups(axisY)
  const members = groups.reduce((sum, group) => sum + group.length, 0)
  const barLike = axisY.filter(
    (axis) => BAR_TYPES.includes(axis.chartType) || STACKED_TYPES.includes(axis.chartType),
  ).length
  const slots = Math.max(1, barLike - members + groups.length)

  return base + (slots - 1) * 10
}

/** 선수 묶음(페이지) 나누기 */
export function chunkVerticalRows<T>(rows: T[], perPage: number): T[][] {
  const size = Math.max(1, Math.floor(perPage))
  const chunks: T[][] = []
  for (let start = 0; start < rows.length; start += size) {
    chunks.push(rows.slice(start, start + size))
  }

  return chunks.length ? chunks : [[]]
}

const labelWidthFor = (rows: ChartRow[], fontSize: number) => {
  const longest = rows.reduce((max, row) => Math.max(max, row.name.length), 0)
  const scale = fontSize / 11

  return Math.min(
    LABEL_MAX_PX * scale,
    Math.max(LABEL_MIN_PX * scale, longest * LABEL_CHAR_PX * scale),
  )
}

/** 공백 기준으로 줄을 나눈다 — 자르지 않는다. 한 단어가 한 줄보다 길면 글자 단위로 나눈다 (2026-10-01 요청: 말줄임 없이) */
const wrapLabel = (text: string, maxChars: number): string[] => {
  const limit = Math.max(1, maxChars)
  if (text.length <= limit) {
    return [text]
  }
  const lines: string[] = []
  let current = ""
  text.split(/\s+/).forEach((word) => {
    let rest = word
    while (rest.length > limit) {
      if (current) {
        lines.push(current)
        current = ""
      }
      lines.push(rest.slice(0, limit))
      rest = rest.slice(limit)
    }
    const next = current ? `${current} ${rest}` : rest
    if (next.length > limit && current) {
      lines.push(current)
      current = rest
    } else {
      current = next
    }
  })
  if (current) {
    lines.push(current)
  }

  return lines
}

const formatValue = (value: number | null, decimalPoint: number) =>
  value == null ? "" : getLocaleString(value, decimalPoint)

/**
 * container 안을 비우고 SVG 하나를 그려 돌려준다. 호출한 쪽이 크기(width)·행 묶음을 정한다.
 */
export function drawVerticalPlayerChart(
  container: HTMLElement,
  {
    rows,
    axisY,
    width,
    rowHeight: rowHeightInput,
    valuePositionType = VALUE_POSITION_TYPE.HIDE_VALUES,
    decimalPoint = null,
    highest,
    highestStackedSum,
    fontSize = 11,
    showAxis = true,
    onRowHover,
  }: VerticalPlayerChartInput,
): SVGSVGElement | null {
  container.innerHTML = ""
  // 선수 행이 없으면 아무것도 그리지 않는다 — 데이터를 받는 동안 축만 덩그러니 보였다 (2026-10-01 보고)
  if (width < 120 || !axisY.length || !rows.length) {
    return null
  }

  const rowHeight = rowHeightInput ?? rowHeightFor(axisY)
  const series: Series[] = axisY.map((axis, index) => ({ axis, index }))
  // 누적 막대는 같은 단위끼리만 쌓고(2개 이상), 짝이 없는 누적은 보통 막대로 — 가로 엔진과 같은 규칙
  const stackGroups = stackedUnitGroups(axisY).map((group) => group.map((index) => series[index]))
  const stackedMembers = new Set(stackGroups.flat().map((s) => s.index))
  const bars = series.filter(
    (s) =>
      BAR_TYPES.includes(s.axis.chartType) ||
      (STACKED_TYPES.includes(s.axis.chartType) && !stackedMembers.has(s.index)),
  )
  const others = series.filter(
    (s) => !BAR_TYPES.includes(s.axis.chartType) && !STACKED_TYPES.includes(s.axis.chartType),
  )
  // 값 표시 위치 — 없음 / 막대 안 가운데(insidePlots) / 막대 끝(abovePlots) / 모아서 보기(separateTable: 오른쪽 값 열)
  const valueMode: "none" | "inside" | "end" | "column" =
    valuePositionType === VALUE_POSITION_TYPE.HIDE_VALUES
      ? "none"
      : valuePositionType === VALUE_POSITION_TYPE.IN_BAR ||
          valuePositionType === VALUE_POSITION_TYPE.IN_CHART_IN_ORDER
        ? "inside"
        : valuePositionType === VALUE_POSITION_TYPE.ABOVE ||
            valuePositionType === VALUE_POSITION_TYPE.BELOW
          ? "column"
          : "end"
  const showValues = valueMode === "end"

  // 왼쪽 이름 열 — 선수 이름이 기준이되, 축 줄의 시리즈 이름이 두 줄에 안 들어가면 그만큼 넓힌다 (2026-10-01 요청)
  const axisNameFont = Math.max(9, fontSize - 2)
  const longestAxisName = showAxis
    ? Math.max(
        axisY.reduce((max, axis) => Math.max(max, axis.name.length), 0),
        ...stackedUnitGroups(axisY).map(
          (group) => group.map((index) => axisY[index].name).join(" + ").length,
        ),
      )
    : 0
  // 두 줄에 들어갈 폭을 잡되 차트 폭의 40% 까지만 — 그래도 안 들어가면 세 줄 이상으로 내려 쓴다 (2026-10-01 요청)
  const axisNameWidth = Math.ceil(longestAxisName / 2 + 2) * LABEL_CHAR_PX * (axisNameFont / 11) + 8
  const labelWidth = Math.max(
    labelWidthFor(rows, fontSize),
    Math.min(axisNameWidth, Math.floor(width * 0.4)),
  )
  const margin = VERTICAL_CHART_MARGIN
  const innerLeft = margin.left + labelWidth + 8
  // 모아서 보기는 오른쪽에 시리즈마다 값 열 — 열 폭은 그 열에서 가장 긴 값 글자 수에 맞춰 촘촘하게, 왼쪽 정렬 (2026-10-01 요청)
  const valueFont = Math.max(9, fontSize - 1)
  const valueColumnWidths =
    valueMode === "column"
      ? axisY.map((axis, index) => {
          const longest = rows.reduce(
            (max, row) =>
              Math.max(
                max,
                formatValue(
                  row.data[index],
                  decimalPoint == null ? axis.decimalPoint : decimalPoint,
                ).length,
              ),
            1,
          )

          return longest * valueFont * 0.62 + 8
        })
      : []
  const valueColumnWidth = valueColumnWidths.reduce((sum, w) => sum + w, 0)
  const innerWidth = Math.max(40, width - innerLeft - margin.right - valueColumnWidth)
  const rowsHeight = rows.length * rowHeight
  const height = measureVerticalChartHeight(rows.length, axisY, rowHeight, showAxis)

  // ── 스케일: 시리즈마다 하나. 수동 스케일 > 전체 페이지 최대(highest) > 이 묶음 최대
  const maxOf = (s: Series) => {
    const { axis, index } = s
    if (axis.manualScaleActive && axis.manualScaleValue != null) {
      return axis.manualScaleValue
    }
    const shared = highest?.[index]
    if (shared != null && shared > 0) {
      return shared
    }

    return rows.reduce((max, row) => Math.max(max, row.data[index] ?? 0), 0)
  }
  const scaleFor = (max: number) =>
    d3
      .scaleLinear()
      .domain([0, max > 0 ? max : 1])
      .nice()
      .range([0, innerWidth])
  const seriesScale = new Map<number, d3.ScaleLinear<number, number>>()
  series.forEach((s) => seriesScale.set(s.index, scaleFor(maxOf(s))))
  // 누적 묶음마다 스케일 하나 — 묶음이 하나면 호출한 쪽이 넘긴 전체 페이지 합 최대(highestStackedSum)를 쓴다
  const stackScales = stackGroups.map((group) => {
    const allManual = group.every(
      (s) => s.axis.manualScaleActive && s.axis.manualScaleValue != null,
    )
    const rowMax = rows.reduce(
      (max, row) =>
        Math.max(
          max,
          group.reduce((sum, s) => sum + (row.data[s.index] ?? 0), 0),
        ),
      0,
    )
    const stackedMax = allManual
      ? group.reduce((sum, s) => sum + (s.axis.manualScaleValue ?? 0), 0)
      : stackGroups.length === 1
        ? (highestStackedSum ?? rowMax)
        : rowMax

    return scaleFor(stackedMax)
  })

  // ── 행 밴드
  const y0 = d3
    .scaleBand<number>()
    .domain(rows.map((_, i) => i))
    .range([0, rowsHeight])
    .paddingInner(0.22)
    .paddingOuter(0.08)
  const barSlots: string[] = [
    ...bars.map((s) => `bar-${s.index}`),
    ...stackGroups.map((_, gi) => `stack-${gi}`),
  ]
  const y1 = d3.scaleBand<string>().domain(barSlots).range([0, y0.bandwidth()]).padding(0.12)
  const rowCenter = (i: number) => (y0(i) ?? 0) + y0.bandwidth() / 2

  const svg = d3
    .create("svg")
    .attr("viewBox", `0 0 ${width} ${height}`)
    .attr("width", width)
    .attr("height", height)
    .attr("class", "VerticalPlayerChart__svg")
    .style("font-family", "inherit")
  const root = svg.append("g").attr("transform", `translate(${innerLeft}, ${margin.top})`)

  // 세로 격자 — 첫 시리즈 눈금 기준
  const gridScale = seriesScale.get(series[0].index) ?? stackScales[0]
  if (gridScale) {
    root
      .append("g")
      .attr("class", "VerticalPlayerChart__grid")
      .selectAll("line")
      .data(gridScale.ticks(5))
      .join("line")
      .attr("x1", (d) => gridScale(d))
      .attr("x2", (d) => gridScale(d))
      .attr("y1", 0)
      .attr("y2", rowsHeight)
      .attr("stroke", colors.neutral.grey["200"])
      .attr("stroke-width", 1)
  }

  // 선수 이름
  svg
    .append("g")
    .attr("transform", `translate(${margin.left}, ${margin.top})`)
    .selectAll("text")
    .data(rows)
    .join("text")
    .attr("x", labelWidth)
    .attr("y", (_, i) => rowCenter(i))
    .attr("dominant-baseline", "central")
    .attr("text-anchor", "end")
    .attr("font-size", fontSize)
    .attr("fill", colors.neutral.grey["800"])
    .text((row) => {
      const maxChars = Math.floor(labelWidth / (LABEL_CHAR_PX * (fontSize / 11)))

      return row.name.length > maxChars
        ? `${row.name.slice(0, Math.max(1, maxChars - 1))}…`
        : row.name
    })

  // 값 글자 — 시리즈 색 + 흰 테두리(paint-order stroke)로 막대 안·밖 어디서나 읽힌다 (2026-10-01 요청)
  const valueText = (
    parent: d3.Selection<SVGGElement, undefined, null, undefined>,
    x: number,
    y: number,
    text: string,
    color: string,
  ) =>
    parent
      .append("text")
      .attr("x", x)
      .attr("y", y)
      .attr("dominant-baseline", "central")
      .attr("font-size", Math.max(9, fontSize - 1))
      .attr("font-weight", 600)
      .attr("fill", color)
      .attr("stroke", colors.neutral.white)
      .attr("stroke-width", 2.5)
      .attr("stroke-linejoin", "round")
      .attr("paint-order", "stroke")
      .text(text)

  const decimalFor = (axis: AxisY) => (decimalPoint == null ? axis.decimalPoint : decimalPoint)

  // ── 막대
  bars.forEach((s) => {
    const scale = seriesScale.get(s.index)!
    const g = root.append("g").attr("class", "VerticalPlayerChart__bars")
    rows.forEach((row, i) => {
      const value = row.data[s.index]
      if (value == null) {
        return
      }
      const y = (y0(i) ?? 0) + (y1(`bar-${s.index}`) ?? 0)
      const w = Math.max(0, scale(value))
      g.append("rect")
        .attr("x", 0)
        .attr("y", y)
        .attr("width", w)
        .attr("height", y1.bandwidth())
        .attr("fill", s.axis.color)
      if (showValues) {
        valueText(
          g,
          w + 4,
          y + y1.bandwidth() / 2,
          formatValue(value, decimalFor(s.axis)),
          s.axis.color,
        )
      } else if (valueMode === "inside" && w > 24 && y1.bandwidth() >= 9) {
        valueText(
          g,
          w / 2,
          y + y1.bandwidth() / 2,
          formatValue(value, decimalFor(s.axis)),
          s.axis.color,
        ).attr("text-anchor", "middle")
      }
    })
  })

  // ── 누적 막대 — 묶음마다. 값은 In chart = 조각 가운데, Above plots = 조각 오른쪽 끝. 합계는 그리지 않는다 (2026-10-01 요청)
  stackGroups.forEach((group, gi) => {
    const scale = stackScales[gi]
    const g = root.append("g").attr("class", "VerticalPlayerChart__stacked")
    rows.forEach((row, i) => {
      const y = (y0(i) ?? 0) + (y1(`stack-${gi}`) ?? 0)
      let acc = 0
      group.forEach((s) => {
        const value = row.data[s.index] ?? 0
        const x0 = scale(acc)
        const x1 = scale(acc + value)
        g.append("rect")
          .attr("x", x0)
          .attr("y", y)
          .attr("width", Math.max(0, x1 - x0))
          .attr("height", y1.bandwidth())
          .attr("fill", s.axis.color)
        if (x1 - x0 > 24 && y1.bandwidth() >= 9) {
          const text = formatValue(value, decimalFor(s.axis))
          if (valueMode === "inside") {
            valueText(g, (x0 + x1) / 2, y + y1.bandwidth() / 2, text, s.axis.color).attr(
              "text-anchor",
              "middle",
            )
          } else if (valueMode === "end") {
            valueText(g, x1 - 3, y + y1.bandwidth() / 2, text, s.axis.color).attr(
              "text-anchor",
              "end",
            )
          }
        }
        acc += value
      })
    })
  })

  // ── 포인트·라인·에어리어 — 행 가운데 높이에 그린다
  others.forEach((s) => {
    const scale = seriesScale.get(s.index)!
    const points = rows
      .map((row, i) => ({ i, value: row.data[s.index] }))
      .filter((p): p is { i: number; value: number } => p.value != null)
    const g = root.append("g").attr("class", `VerticalPlayerChart__${s.axis.chartType}`)
    if (s.axis.chartType === CHART_TYPE.AREA && points.length > 1) {
      const area = d3
        .area<{ i: number; value: number }>()
        .x0(0)
        .x1((p) => scale(p.value))
        .y((p) => rowCenter(p.i))
        .curve(d3.curveMonotoneY)
      g.append("path")
        .attr("d", area(points) ?? "")
        .attr("fill", s.axis.color)
        .attr("fill-opacity", 0.25)
    }
    if (
      (s.axis.chartType === CHART_TYPE.LINE || s.axis.chartType === CHART_TYPE.AREA) &&
      points.length > 1
    ) {
      const line = d3
        .line<{ i: number; value: number }>()
        .x((p) => scale(p.value))
        .y((p) => rowCenter(p.i))
        .curve(d3.curveMonotoneY)
      g.append("path")
        .attr("d", line(points) ?? "")
        .attr("fill", "none")
        .attr("stroke", s.axis.color)
        .attr("stroke-width", 2)
    }
    points.forEach((p) => {
      g.append("circle")
        .attr("cx", scale(p.value))
        .attr("cy", rowCenter(p.i))
        .attr("r", s.axis.chartType === CHART_TYPE.POINT ? 4.5 : 3)
        .attr("fill", s.axis.color)
        .attr("stroke", colors.neutral.white)
        .attr("stroke-width", 1)
      if (showValues) {
        valueText(
          g,
          scale(p.value) + 7,
          rowCenter(p.i),
          formatValue(p.value, decimalFor(s.axis)),
          s.axis.color,
        )
      }
    })
  })

  // ── 시리즈 축 — 아래쪽에 한 줄씩. 막대·기타는 시리즈마다, 누적 막대는 묶음에 하나
  const axisEntries: Array<{ scale: d3.ScaleLinear<number, number>; color: string; name: string }> =
    [
      ...bars.map((s) => ({
        scale: seriesScale.get(s.index)!,
        color: s.axis.color,
        name: s.axis.name,
      })),
      ...stackGroups.map((group, gi) => ({
        scale: stackScales[gi],
        color: colors.neutral.grey["700"],
        name: group.map((s) => s.axis.name).join(" + "),
      })),
      ...others.map((s) => ({
        scale: seriesScale.get(s.index)!,
        color: s.axis.color,
        name: s.axis.name,
      })),
    ]
  // 모아서 보기 — 오른쪽 값 열(시리즈마다 한 열). 열 머리말 대신 시리즈 색으로 구분한다(좁은 열에서 이름이 겹침)
  if (valueMode === "column") {
    const columns = root.append("g").attr("class", "VerticalPlayerChart__values")
    let x = innerWidth + margin.right / 2
    axisY.forEach((axis, index) => {
      const columnX = x
      x += valueColumnWidths[index] ?? 0
      rows.forEach((row, i) => {
        valueText(
          columns,
          columnX,
          rowCenter(i),
          formatValue(row.data[index], decimalFor(axis)),
          axis.color,
        )
      })
    })
  }

  ;(showAxis ? axisEntries : []).forEach((entry, order) => {
    const y = rowsHeight + order * VERTICAL_CHART_AXIS_PX
    const g = root.append("g").attr("transform", `translate(0, ${y})`)
    g.call(
      d3
        .axisBottom(entry.scale)
        .ticks(5)
        .tickSize(4)
        .tickFormat((d) => getLocaleString(Number(d), 1)),
    )
    g.selectAll("text")
      .attr("font-size", Math.max(9, fontSize - 1))
      .attr("fill", entry.color)
    g.selectAll("line, path").attr("stroke", entry.color)
    // 시리즈 이름은 축 줄의 왼쪽(선수 이름 열 자리)에, 길면 두 줄로 잘리지 않게 (2026-10-01 요청)
    const nameFont = axisNameFont
    const maxChars = Math.max(6, Math.floor((labelWidth - 8) / (LABEL_CHAR_PX * (nameFont / 11))))
    const lines = wrapLabel(entry.name, maxChars)
    // 줄 수만큼 위로 올려 가운데(12px)에 맞춘다 — 세 줄이면 2·12·22px
    const nameText = g
      .append("text")
      .attr("x", -8)
      .attr("y", 12 - ((lines.length - 1) * (nameFont + 1)) / 2)
      .attr("dominant-baseline", "central")
      .attr("text-anchor", "end")
      .attr("font-size", nameFont)
      .attr("fill", entry.color)
    lines.forEach((line, li) => {
      nameText
        .append("tspan")
        .attr("x", -8)
        .attr("dy", li === 0 ? 0 : nameFont + 1)
        .text(line)
    })
  })

  // ── 행 호버
  if (onRowHover) {
    root
      .append("g")
      .attr("class", "VerticalPlayerChart__hover")
      .selectAll("rect")
      .data(rows)
      .join("rect")
      .attr("x", -innerLeft)
      .attr("y", (_, i) => y0(i) ?? 0)
      .attr("width", width)
      .attr("height", y0.bandwidth())
      .attr("fill", "transparent")
      .on("mouseenter", (event: MouseEvent, row) => onRowHover(rows.indexOf(row), event))
      .on("mousemove", (event: MouseEvent, row) => onRowHover(rows.indexOf(row), event))
      .on("mouseleave", () => onRowHover(null, null))
  }

  const node = svg.node()
  if (node) {
    container.appendChild(node)
  }

  return node
}
